import React from 'react';
import {
  Box,
  Text,
  Heading,
  SimpleGrid,
  VStack,
  Divider,
} from '@chakra-ui/react';
import { useBinderCardColors } from '../../binder_report/hooks/useBinderCardColors';

export interface RatingInfoField {
  field: string;
  value: string;
  section?: string;
}

export interface MortgageeInfo {
  name: string;
  address: string;
}

export interface LocationData {
  locationNumber: string;
  riskAddress: string;
  coverageFormType: string;
  ratingInfo: RatingInfoField[];
  mortgagees: MortgageeInfo[];
}

interface HomeLocationCardProps {
  locationData: LocationData;
}

const HomeLocationCard: React.FC<HomeLocationCardProps> = ({
  locationData,
}) => {
  const colors = useBinderCardColors();

  const renderFields = (fields: RatingInfoField[]) => (
    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
      {fields.map((item, index) => (
        <Box key={index}>
          <Text
            fontSize="xs"
            color={colors.descriptionColor}
            mb={1}
            fontWeight="medium"
          >
            {item.field}
          </Text>
          <Text fontSize="sm" color={colors.textColor} fontWeight="semibold">
            {item.value || 'N/A'}
          </Text>
        </Box>
      ))}
    </SimpleGrid>
  );

  // Group fields by section
  const mainFields = locationData.ratingInfo.filter((item) => !item.section);
  const upgradesFields = locationData.ratingInfo.filter(
    (item) => item.section === 'upgrades',
  );
  const heatingFields = locationData.ratingInfo.filter(
    (item) => item.section === 'heating',
  );

  return (
    <Box
      borderRadius="md"
      borderWidth="1px"
      borderColor={colors.borderColor}
      overflow="hidden"
      mb={4}
    >
      {/* Header */}
      <Box
        p={4}
        bg={colors.emptyFieldBgColor}
        borderBottomWidth="1px"
        borderColor={colors.borderColor}
      >
        <Heading size="sm" color={colors.textColor} mb={2}>
          Location {locationData.locationNumber}
        </Heading>
        <VStack align="stretch" spacing={1}>
          <Box>
            <Text fontSize="xs" color={colors.descriptionColor}>
              Risk Address
            </Text>
            <Text fontSize="sm" color={colors.textColor} fontWeight="semibold">
              {locationData.riskAddress}
            </Text>
          </Box>
          <Box>
            <Text fontSize="xs" color={colors.descriptionColor}>
              Coverage Form Type
            </Text>
            <Text fontSize="sm" color={colors.textColor} fontWeight="semibold">
              {locationData.coverageFormType}
            </Text>
          </Box>
        </VStack>
      </Box>

      {/* Rating Information */}
      <VStack align="stretch" spacing={4} p={4}>
        <Box>
          <Heading size="xs" color={colors.textColor} mb={3}>
            Rating Information
          </Heading>
          {mainFields.length > 0 && renderFields(mainFields)}
        </Box>

        {/* Upgrades Section */}
        {upgradesFields.length > 0 && (
          <>
            <Divider />
            <Box>
              <Heading size="xs" color={colors.textColor} mb={3}>
                Upgrades
              </Heading>
              {renderFields(upgradesFields)}
            </Box>
          </>
        )}

        {/* Heating Section */}
        {heatingFields.length > 0 && (
          <>
            <Divider />
            <Box>
              <Heading size="xs" color={colors.textColor} mb={3}>
                Heating & Electrical
              </Heading>
              {renderFields(heatingFields)}
            </Box>
          </>
        )}

        {/* Mortgagees Section */}
        {locationData.mortgagees.length > 0 && (
          <>
            <Divider />
            <Box>
              <Heading size="xs" color={colors.textColor} mb={3}>
                Mortgagees
              </Heading>
              <VStack align="stretch" spacing={3}>
                {locationData.mortgagees.map((mortgagee, index) => (
                  <Box key={index}>
                    <Text
                      fontSize="xs"
                      color={colors.descriptionColor}
                      mb={1}
                      fontWeight="medium"
                    >
                      {index + 1}. {mortgagee.name}
                    </Text>
                    <Text
                      fontSize="sm"
                      color={colors.textColor}
                      fontWeight="semibold"
                    >
                      {mortgagee.address}
                    </Text>
                  </Box>
                ))}
              </VStack>
            </Box>
          </>
        )}
      </VStack>
    </Box>
  );
};

export default HomeLocationCard;
